import { useState, useEffect } from 'react';
import { useEditorStore } from '../../stores/editorStore';

type EntityPrefabData = {
  category?: string;
  class?: string;
  texture?: string;
  hp?: number;
  maxHp?: number;
  allegiance?: string;
  detectionRange?: number;
  behaviors?: Record<string, unknown>;
  [key: string]: unknown;
};

type EntityPrefabFile = {
  entities?: Record<string, EntityPrefabData>;
};

const ATTR_LABELS: Record<string, string> = {
  hp: 'Vida',
  maxHp: 'Vida max.',
  speed: 'Velocidad',
  damage: 'Daño',
  fireRate: 'Cadencia',
  projectileSpeed: 'Vel. disparo',
  detectionRange: 'Rango',
  allegiance: 'Bando',
  category: 'Categoria',
  texture: 'Textura',
  class: 'Clase',
  weight: 'Peso',
  bioMatter: 'Biomateria',
};

const EDITABLE_ATTRIBUTES = [
  { key: 'health', label: 'Vida', fallbackPath: 'hp' },
  { key: 'maxHealth', label: 'Vida max.', fallbackPath: 'maxHp' },
  { key: 'speed', label: 'Velocidad', fallbackPath: 'behaviors.walker.speed' },
  { key: 'range', label: 'Rango', fallbackPath: 'detectionRange' },
  { key: 'rateOfFire', label: 'Cadencia', fallbackPath: 'behaviors.shooter.fireRate' },
  { key: 'biomoney', label: 'Biomateria', fallbackPath: 'behaviors.abductable.bioMatter' },
  { key: 'weight', label: 'Peso', fallbackPath: 'behaviors.abductable.weight' },
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function deepMerge<T extends Record<string, unknown>>(base: T, override: Record<string, unknown>): T {
  const next = { ...base };

  for (const [key, value] of Object.entries(override)) {
    if (key.startsWith('_')) continue;
    const current = next[key];
    if (isRecord(current) && isRecord(value)) {
      next[key] = deepMerge(current, value);
    } else {
      next[key] = value;
    }
  }

  return next;
}

function formatValue(value: unknown): string {
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(2);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return `${value.length} items`;
  if (isRecord(value)) return '...';
  if (value === null) return 'null';
  return String(value);
}

function getNestedValue(source: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((current, key) => {
    if (!isRecord(current)) return undefined;
    return current[key];
  }, source);
}

function collectAttributeRows(entityData: Record<string, unknown>, prefab?: EntityPrefabData) {
  const properties = isRecord(entityData.properties) ? entityData.properties : {};
  const resolved = deepMerge((prefab ?? {}) as Record<string, unknown>, {});

  if (properties.health !== undefined) {
    resolved.hp = properties.health;
    if (resolved.maxHp === undefined) resolved.maxHp = properties.health;
  }
  if (properties.maxHealth !== undefined) resolved.maxHp = properties.maxHealth;
  if (properties.speed !== undefined) {
    const behaviors = isRecord(resolved.behaviors) ? { ...resolved.behaviors } : {};
    for (const behaviorName of ['walker', 'flyer']) {
      const behavior = behaviors[behaviorName];
      if (isRecord(behavior)) {
        behaviors[behaviorName] = { ...behavior, speed: properties.speed };
      }
    }
    resolved.behaviors = behaviors;
  }
  if (properties.range !== undefined) resolved.detectionRange = properties.range;

  const rows: Array<{ key: string; label: string; value: unknown; source: string }> = [];
  const addRow = (key: string, value: unknown, source: string) => {
    if (value === undefined || isRecord(value) || Array.isArray(value)) return;
    rows.push({
      key: `${source}:${key}`,
      label: ATTR_LABELS[key.split('.').at(-1) ?? key] ?? key,
      value,
      source,
    });
  };

  for (const key of ['hp', 'maxHp', 'category', 'allegiance', 'detectionRange', 'texture', 'class']) {
    addRow(key, resolved[key], 'Prefab');
  }

  for (const path of [
    'behaviors.walker.speed',
    'behaviors.flyer.speed',
    'behaviors.shooter.damage',
    'behaviors.shooter.fireRate',
    'behaviors.shooter.projectileSpeed',
    'behaviors.abductable.weight',
    'behaviors.abductable.bioMatter',
  ]) {
    addRow(path, getNestedValue(resolved, path), 'Comportamiento');
  }

  for (const [key, value] of Object.entries(properties)) {
    addRow(key, value, 'Instancia');
  }

  const seen = new Set<string>();
  return rows.filter((row) => {
    const uniqueKey = `${row.label}:${formatValue(row.value)}`;
    if (seen.has(uniqueKey)) return false;
    seen.add(uniqueKey);
    return true;
  });
}

function collectEditableAttributeRows(entityData: Record<string, unknown>, prefab?: EntityPrefabData) {
  const properties = isRecord(entityData.properties) ? entityData.properties : {};
  const resolved = deepMerge((prefab ?? {}) as Record<string, unknown>, {});

  return EDITABLE_ATTRIBUTES.map((attribute) => {
    const instanceValue = properties[attribute.key];
    const fallbackValue = getNestedValue(resolved, attribute.fallbackPath);

    return {
      key: attribute.key,
      label: attribute.label,
      value: instanceValue ?? fallbackValue ?? '',
      source: instanceValue !== undefined ? 'Instancia' : 'Prefab',
    };
  });
}

export default function PropertiesPanel() {
  const [expanded, setExpanded] = useState(true);
  const [attributesExpanded, setAttributesExpanded] = useState(true);
  const [entityPrefabs, setEntityPrefabs] = useState<Record<string, EntityPrefabData>>({});
  const {
    selectedIds,
    objects,
    entities,
    mode,
    updateObjectProperty,
    updateEntityInstanceProperty,
    editorLayers,
    entityLayerAssignments,
    setEntityLayer,
  } = useEditorStore();

  // Get selected object
  const selectedObject = selectedIds.length === 1
    ? (mode === 'layouts'
        ? objects.find((o) => o.id === selectedIds[0])
        : entities.find((e) => e.id === selectedIds[0]))
    : null;

  const data = selectedObject?.data;
  const selectedEntityId =
    mode === 'entities' && data ? ((data as { entityId?: string }).entityId ?? '') : '';
  const selectedPrefab = selectedEntityId
    ? entityPrefabs[selectedEntityId] ?? entityPrefabs[selectedEntityId.toLowerCase()]
    : undefined;
  const attributeRows =
    mode === 'entities' && data
      ? collectAttributeRows(data as Record<string, unknown>, selectedPrefab)
      : [];
  const editableAttributeRows =
    mode === 'entities' && data
      ? collectEditableAttributeRows(data as Record<string, unknown>, selectedPrefab)
      : [];

  // Local state for inputs (to allow typing without immediate updates)
  const [localValues, setLocalValues] = useState<Record<string, string>>({});
  const [localAttributeValues, setLocalAttributeValues] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;

    const loadEntityPrefabs = async () => {
      try {
        const [baseRes, overridesRes] = await Promise.all([
          fetch('/assets/data/entities-c3data.json'),
          fetch('/assets/data/entities-overrides.json'),
        ]);
        const baseData = baseRes.ok ? ((await baseRes.json()) as EntityPrefabFile) : {};
        const overrideData = overridesRes.ok ? ((await overridesRes.json()) as EntityPrefabFile) : {};
        const merged: Record<string, EntityPrefabData> = {};

        for (const [key, value] of Object.entries(baseData.entities ?? {})) {
          merged[key] = value;
          merged[key.toLowerCase()] = value;
        }

        for (const [key, value] of Object.entries(overrideData.entities ?? {})) {
          if (key.startsWith('_')) continue;
          const base = merged[key] ?? merged[key.toLowerCase()] ?? {};
          const next = deepMerge(base as Record<string, unknown>, value as Record<string, unknown>);
          merged[key] = next as EntityPrefabData;
          merged[key.toLowerCase()] = next as EntityPrefabData;
        }

        if (!cancelled) setEntityPrefabs(merged);
      } catch {
        if (!cancelled) setEntityPrefabs({});
      }
    };

    loadEntityPrefabs();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (data) {
      setLocalValues({
        x: String(data.x),
        y: String(data.y),
        width: String(data.width),
        height: String(data.height),
      });
      setLocalAttributeValues(
        Object.fromEntries(
          editableAttributeRows.map((row) => [row.key, row.value === '' ? '' : String(row.value)])
        )
      );
    }
  }, [data, selectedPrefab]);

  const handleChange = (prop: string, value: string) => {
    setLocalValues((prev) => ({ ...prev, [prop]: value }));
  };

  const handleBlur = (prop: string) => {
    if (!selectedObject) return;
    const numValue = parseFloat(localValues[prop] ?? '0');
    if (!isNaN(numValue)) {
      updateObjectProperty(selectedObject.id, prop, numValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, prop: string) => {
    if (e.key === 'Enter') {
      handleBlur(prop);
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleEntityLayerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!selectedObject || mode !== 'entities') return;
    setEntityLayer(selectedObject.id, e.target.value);
  };

  const handleAttributeChange = (prop: string, value: string) => {
    setLocalAttributeValues((prev) => ({ ...prev, [prop]: value }));
  };

  const handleAttributeBlur = (prop: string) => {
    if (!selectedObject || mode !== 'entities') return;
    const rawValue = localAttributeValues[prop] ?? '';
    const numValue = parseFloat(rawValue);
    if (!isNaN(numValue)) {
      updateEntityInstanceProperty(selectedObject.id, prop, numValue);
    }
  };

  const handleAttributeKeyDown = (e: React.KeyboardEvent, prop: string) => {
    if (e.key === 'Enter') {
      handleAttributeBlur(prop);
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div className="properties-panel-stack">
      <div className="panel properties-panel-main">
        <div className="panel-header" onClick={() => setExpanded(!expanded)}>
          <span>Properties</span>
          <span>{expanded ? '▼' : '▶'}</span>
        </div>
        {expanded && (
        <div className="panel-content">
          {selectedIds.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              Select an object to edit
            </p>
          ) : selectedIds.length > 1 ? (
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              {selectedIds.length} objects selected
            </p>
          ) : data ? (
            <>
              <div className="property-row">
                <span className="property-label">Type</span>
                <span style={{ color: 'var(--accent)' }}>
                  {mode === 'layouts'
                    ? (data as { type?: string }).type
                    : (data as { entityId?: string }).entityId}
                </span>
              </div>

              {mode === 'entities' && (
                <div className="property-row">
                  <span className="property-label">Layer</span>
                  <select
                    className="property-input"
                    value={entityLayerAssignments[selectedObject.id] ?? ''}
                    onChange={handleEntityLayerChange}
                  >
                    {editorLayers.map((layerName) => (
                      <option key={layerName} value={layerName}>
                        {layerName}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="property-row">
                <span className="property-label">X</span>
                <input
                  type="number"
                  className="property-input"
                  value={localValues.x ?? ''}
                  onChange={(e) => handleChange('x', e.target.value)}
                  onBlur={() => handleBlur('x')}
                  onKeyDown={(e) => handleKeyDown(e, 'x')}
                />
              </div>

              <div className="property-row">
                <span className="property-label">Y</span>
                <input
                  type="number"
                  className="property-input"
                  value={localValues.y ?? ''}
                  onChange={(e) => handleChange('y', e.target.value)}
                  onBlur={() => handleBlur('y')}
                  onKeyDown={(e) => handleKeyDown(e, 'y')}
                />
              </div>

              <div className="property-row">
                <span className="property-label">Width</span>
                <input
                  type="number"
                  className="property-input"
                  value={localValues.width ?? ''}
                  onChange={(e) => handleChange('width', e.target.value)}
                  onBlur={() => handleBlur('width')}
                  onKeyDown={(e) => handleKeyDown(e, 'width')}
                />
              </div>

              <div className="property-row">
                <span className="property-label">Height</span>
                <input
                  type="number"
                  className="property-input"
                  value={localValues.height ?? ''}
                  onChange={(e) => handleChange('height', e.target.value)}
                  onBlur={() => handleBlur('height')}
                  onKeyDown={(e) => handleKeyDown(e, 'height')}
                />
              </div>

              {mode === 'layouts' && (data as { angle?: number }).angle !== undefined && (
                <div className="property-row">
                  <span className="property-label">Angle</span>
                  <input
                    type="number"
                    className="property-input"
                    value={((data as { angle: number }).angle * 180 / Math.PI).toFixed(1)}
                    readOnly
                  />
                </div>
              )}
            </>
          ) : null}
          </div>
        )}
      </div>

      <div className="panel attributes-panel">
        <div className="panel-header" onClick={() => setAttributesExpanded(!attributesExpanded)}>
          <span>Atributos</span>
          <span>{attributesExpanded ? '▼' : '▶'}</span>
        </div>
        {attributesExpanded && (
          <div className="panel-content expanded">
            {selectedIds.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                Selecciona una entidad
              </p>
            ) : selectedIds.length > 1 ? (
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                Selecciona solo una entidad
              </p>
            ) : mode !== 'entities' ? (
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                Disponible para entidades
              </p>
            ) : editableAttributeRows.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                Sin atributos registrados
              </p>
            ) : (
              <div className="attribute-list">
                {editableAttributeRows.map((row) => (
                  <div className="attribute-row" key={row.key}>
                    <div className="attribute-main">
                      <span className="attribute-label">{row.label}</span>
                      <input
                        type="number"
                        className="attribute-input"
                        value={localAttributeValues[row.key] ?? ''}
                        placeholder={row.value === '' ? '' : String(row.value)}
                        onChange={(e) => handleAttributeChange(row.key, e.target.value)}
                        onBlur={() => handleAttributeBlur(row.key)}
                        onKeyDown={(e) => handleAttributeKeyDown(e, row.key)}
                      />
                    </div>
                    <span className="attribute-source">{row.source}</span>
                  </div>
                ))}
                {attributeRows.map((row) => (
                  <div className="attribute-row" key={row.key}>
                    <div className="attribute-main">
                      <span className="attribute-label">{row.label}</span>
                      <span className="attribute-value">{formatValue(row.value)}</span>
                    </div>
                    <span className="attribute-source">{row.source}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
